import { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { FormFieldSet } from "@progress/kendo-react-form";
import { hyperlinkOpenIcon, plusIcon } from "@progress/kendo-svg-icons";
import {
  ContentLayout,
  ContentHeader,
  ContentHeaderTitle,
  ContentHeaderBreadcrumbs,
  ContentHeaderBackNavigation,
  ContentHeaderActions,
} from "@excent-one/react/layout/content";
import { FormLayout, FormSection, SearchFormLayout } from "@excent-one/react";
import { DataGrid, DataGridColumnProps } from "@excent-one/react/data-grid";
import {
  ControlledDatePicker,
  ControlledInput,
  ControlledComboBox,
} from "@excent-one/react/form/inputs";
import { Button } from "@progress/kendo-react-all";
import { notification } from "@excent-one/react/notification";
import { useAuthContext } from "@excent-one/auth";
import {
  searchTransmittalRecords,
  createTransmittalRecord,
} from "@phrmpc/infrastructure/phrmpc/api/transmittal-records";
import { getAllTransmittalStatuses } from "@phrmpc/infrastructure/phrmpc/api/transmittal-statuses";
import { type TransmittalRecordSummaryDto } from "@phrmpc/infrastructure/phrmpc/models";
import { FilterLogic, FilterOperator } from "@phrmpc/shared";
import { DateTime } from "luxon";
import { getCoordinators, getMyDetails } from "@phrmpc/infrastructure/phrmpc";
import { usePagedGrid } from "@phrmpc/shared/hooks/usePagedGrid";

const defaultSearchValues: TransmittalRecordSummaryDto = {
  date: undefined,
  transmittalId: "",
  issuerId: "",
  receiverId: "",
  statusId: undefined,
};

const buildFilter = (
  values: TransmittalRecordSummaryDto,
  currentUserId: string,
) => {
  const filters: { field: string; operator: number; value: unknown }[] = [];
  if (values.date) {
    filters.push({
      field: "date",
      operator: FilterOperator.Equals,
      value: values.date,
    });
  }
  if (values.transmittalId) {
    filters.push({
      field: "transmittalId",
      operator: FilterOperator.Contains,
      value: values.transmittalId,
    });
  }
  if (values.issuerId) {
    filters.push({
      field: "issuerId",
      operator: FilterOperator.Equals,
      value: values.issuerId,
    });
  }
  if (values.receiverId) {
    filters.push({
      field: "receiverId",
      operator: FilterOperator.Equals,
      value: values.receiverId,
    });
  }
  if (values.statusId != null) {
    filters.push({
      field: "statusId",
      operator: FilterOperator.Equals,
      value: values.statusId,
    });
  }

  const scope = {
    logic: FilterLogic.Or,
    filters: [
      {
        field: "issuerId",
        operator: FilterOperator.Equals,
        value: currentUserId,
      },
      {
        field: "receiverId",
        operator: FilterOperator.Equals,
        value: currentUserId,
      },
    ],
  } as const;

  if (filters.length === 0) return scope;
  return { logic: FilterLogic.And, filters: [scope, ...filters] } as const;
};

const isSearchCriteriaEmpty = (values: TransmittalRecordSummaryDto) =>
  !values.date &&
  !values.transmittalId &&
  !values.issuerId &&
  !values.receiverId &&
  values.statusId == null;

const COLUMNS: DataGridColumnProps<TransmittalRecordSummaryDto>[] = [
  { field: "transmittalId", title: "Transmittal ID", readOnly: true },
  {
    field: "date",
    title: "Date",
    readOnly: true,
    cells: {
      data: (props: any) => (
        <td>
          {props.dataItem.date instanceof Date
            ? DateTime.fromJSDate(props.dataItem.date).toFormat("MM/dd/yyyy")
            : "-"}
        </td>
      ),
    },
  },
  { field: "issuerName", title: "Issuer Name", readOnly: true },
  { field: "receiverName", title: "Recipient Name", readOnly: true },
  { field: "statusName", title: "Status", readOnly: true },
];

const TransmittalSearchForm = () => {
  const navigate = useNavigate();
  const [creating, setCreating] = useState(false);
  const [{ currentUser }] = useAuthContext();

  const { data: myDetails } = useQuery({
    queryKey: ["my-details", currentUser?.id],
    queryFn: () => getMyDetails(),
  });

  const { control, getValues, reset } = useForm<TransmittalRecordSummaryDto>({
    defaultValues: defaultSearchValues,
  });

  const [appliedFilters, setAppliedFilters] =
    useState<TransmittalRecordSummaryDto>(defaultSearchValues);
  const { skip, pageSize, pageNumber, pageable, onPageChange, resetPaging } =
    usePagedGrid();

  const { data: statusOptions } = useQuery({
    queryKey: ["transmittal-statuses"],
    queryFn: () => getAllTransmittalStatuses(),
  });

  const { data: coordinators } = useQuery({
    queryKey: ["coordinators"],
    queryFn: async () => {
      const result = await getCoordinators({
        payload: { pageNumber: 1, pageSize: 500 },
      });
      return result.items ?? [];
    },
  });

  const { data: pagedResult, isLoading } = useQuery({
    queryKey: [
      "transmittal-records",
      appliedFilters,
      skip,
      pageSize,
      myDetails?.id,
    ],
    queryFn: () =>
      searchTransmittalRecords({
        payload: {
          pageNumber,
          pageSize,
          filter: buildFilter(appliedFilters, myDetails!.id!) as any,
        },
      }),
    enabled: Boolean(myDetails?.id),
  });

  const handleSearch = () => {
    const values = getValues();
    if (isSearchCriteriaEmpty(values)) {
      notification.error("Please specify at least one search criterion.");
      return;
    }
    resetPaging();
    setAppliedFilters(values);
  };

  const handleClear = () => {
    reset(defaultSearchValues);
    resetPaging();
    setAppliedFilters(defaultSearchValues);
  };

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await createTransmittalRecord({
        payload: {},
      });
      navigate({
        to: "/document-transmittal/$id/edit",
        params: { id: created.id! },
      });
    } catch (e) {
      console.error(e);
      notification.error(
        "Unable to create transmittal request. Please try again.",
      );
    } finally {
      setCreating(false);
    }
  };

  return (
    <ContentLayout>
      <ContentHeader>
        <ContentHeaderBackNavigation />
        <ContentHeaderTitle title="Search Transmittal Request" />
        <ContentHeaderBreadcrumbs />
        <SearchFormLayout
          buttonProps={{
            search: { onClick: handleSearch },
            clear: { onClick: handleClear },
          }}
        >
          <FormFieldSet cols={2}>
            <ControlledDatePicker control={control} name="date" label="Date" />
            <ControlledInput
              control={control}
              name="transmittalId"
              label="Transmittal ID"
              placeholder="Search by transmittal ID"
              wrapperProps={{ className: "basic-search-field" }}
            />
            {coordinators && (
              <ControlledComboBox
                control={control}
                name="issuerId"
                label="Issuer Name"
                data={coordinators}
                dataItemKey="id"
                textField="fieldName"
              />
            )}
            {coordinators && (
              <ControlledComboBox
                control={control}
                name="receiverId"
                label="Recipient Name"
                data={coordinators}
                dataItemKey="id"
                textField="fieldName"
              />
            )}
            {statusOptions && (
              <ControlledComboBox
                control={control}
                name="statusId"
                label="Status"
                data={statusOptions}
                dataItemKey="id"
                textField="name"
              />
            )}
          </FormFieldSet>
        </SearchFormLayout>
        <ContentHeaderActions>
          <Button
            themeColor="primary"
            svgIcon={plusIcon}
            disabled={creating}
            onClick={handleCreate}
          >
            Create Transmittal Request
          </Button>
        </ContentHeaderActions>
      </ContentHeader>

      <FormLayout>
        <FormSection title="Results">
          <DataGrid
            data={pagedResult?.items ?? []}
            dataItemKey="id"
            columns={COLUMNS}
            canAdd={false}
            hideRowCommandToggle={false}
            commands={[
              {
                command: "view",
                text: "View",
                svgIcon: hyperlinkOpenIcon,
              },
            ]}
            onCommandClicked={{
              view: (e) => {
                if (e.dataItem == null || !e.dataItem.id) return;
                navigate({
                  to: "/document-transmittal/$id/display",
                  params: { id: e.dataItem.id },
                });
              },
            }}
            pageable={pageable}
            skip={skip}
            take={pageSize}
            total={pagedResult?.totalItems ?? 0}
            onPageChange={onPageChange}
            noRecordsMessage="No matching transmittal records found."
            style={{ maxHeight: "500px" }}
            scrollable="virtual"
            loading={isLoading}
          />
        </FormSection>
      </FormLayout>
    </ContentLayout>
  );
};

export default TransmittalSearchForm;
